import type { BossSeason } from "../core/talent-tracking/seasons.js"

import { getCsrfTokenFromDocumentCookie } from "./auth-api"
import { http } from "./http-client"
export type TalentConfigSeason = BossSeason
export type TalentCategory = "offensive" | "defensive" | "strategic"

export type TalentConfigTalent = {
  id: string
  bossId: string
  category: TalentCategory
  tier: number
  description: string
  reward: string
  createdAt: string
  updatedAt: string
}

export type TalentConfigBoss = {
  id: string
  season: TalentConfigSeason
  name: string
  sortOrder: number
  talentIds: string[]
  talents: TalentConfigTalent[]
  talentsByCategory: Record<TalentCategory, TalentConfigTalent[]>
  createdAt: string
  updatedAt: string
}

export type TalentConfigSnapshot = {
  seasons: Array<{
    season: TalentConfigSeason
    bossIds: string[]
    bosses: TalentConfigBoss[]
  }>
  bosses: TalentConfigBoss[]
  bossesById: Record<string, TalentConfigBoss>
  talents: TalentConfigTalent[]
  talentsById: Record<string, TalentConfigTalent>
  talentsByBossId: Record<string, TalentConfigTalent[]>
}

export type TalentConfigExportResult = {
  fileName: string
  bossCount: number
  talentCount: number
}

const toTalentTrackingSlug = (value: string): string => {
  return value.trim().toLowerCase().replace(/\s+/g, "-")
}

export const getTalentConfigBossId = (input: {
  season: TalentConfigSeason
  name: string
}): string => {
  return `${toTalentTrackingSlug(input.season)}:${toTalentTrackingSlug(input.name)}`
}

const getEdenErrorMessage = (error: unknown, fallback: string): string => {
  if (!error || typeof error !== "object") {
    return fallback
  }

  const maybeError = error as { value?: unknown; message?: unknown }
  if (typeof maybeError.message === "string" && maybeError.message.length > 0) {
    return maybeError.message
  }

  if (maybeError.value && typeof maybeError.value === "object") {
    const payload = maybeError.value as {
      error?: { message?: unknown }
      message?: unknown
    }

    if (typeof payload.error?.message === "string") {
      return payload.error.message
    }

    if (typeof payload.message === "string") {
      return payload.message
    }
  }

  return fallback
}

const ensureEdenData = <T>(
  response: { data: T | null | undefined; error: unknown },
  fallbackMessage: string,
): NonNullable<T> => {
  if (response.data != null) {
    return response.data as NonNullable<T>
  }

  throw new Error(getEdenErrorMessage(response.error, fallbackMessage))
}

export const talentConfigApi = {
  getSnapshot: async (): Promise<TalentConfigSnapshot> => {
    const data = ensureEdenData(
      await http.api["talent-config"].get(),
      "Failed to load talent config",
    )

    if ("item" in data) {
      return data.item
    }

    throw new Error("Failed to load talent config")
  },
  exportToJson: async (): Promise<TalentConfigExportResult> => {
    const response = await fetch("/api/talent-config/export", {
      method: "POST",
      credentials: "include",
      headers: {
        "x-csrf-token": getCsrfTokenFromDocumentCookie() ?? "",
      },
    })

    if (!response.ok) {
      let message = "Failed to export talent config"

      try {
        const payload = (await response.json()) as {
          error?: { message?: string }
          message?: string
        }
        message =
          payload.error?.message ??
          payload.message ??
          "Failed to export talent config"
      } catch {
        // Keep fallback message.
      }

      throw new Error(message)
    }

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const fileName =
      response.headers
        .get("content-disposition")
        ?.match(/filename="?([^";]+)"?/)?.[1] ?? "talent-config.json"
    const bossCount = Number(
      response.headers.get("x-talent-config-boss-count") ?? "0",
    )
    const talentCount = Number(
      response.headers.get("x-talent-config-talent-count") ?? "0",
    )

    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = fileName
    document.body.append(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)

    return {
      fileName,
      bossCount,
      talentCount,
    }
  },
  updateBoss: async (
    bossId: string,
    input: {
      season: TalentConfigSeason
      name: string
      sortOrder: number
    },
  ): Promise<TalentConfigBoss> => {
    const data = ensureEdenData(
      await http.api["talent-config"].bosses({ bossId }).put(input),
      "Failed to update boss",
    )

    if ("item" in data) {
      return data.item
    }

    throw new Error("Failed to update boss")
  },
  createBoss: async (input: {
    season: TalentConfigSeason
    name: string
    sortOrder: number
  }): Promise<TalentConfigBoss> => {
    return talentConfigApi.updateBoss(getTalentConfigBossId(input), input)
  },
  deleteBoss: async (bossId: string): Promise<void> => {
    ensureEdenData(
      await http.api["talent-config"].bosses({ bossId }).delete(),
      "Failed to delete boss",
    )
  },
  updateTalent: async (
    talentId: string,
    input: {
      bossId: string
      category: TalentCategory
      tier: number
      description: string
      reward: string
    },
  ): Promise<TalentConfigTalent> => {
    const data = ensureEdenData(
      await http.api["talent-config"].talents({ talentId }).put(input),
      "Failed to update talent",
    )

    if ("item" in data) {
      return data.item
    }

    throw new Error("Failed to update talent")
  },
  createTalent: async (input: {
    bossId: string
    talentId: string
    category: TalentCategory
    tier: number
    description: string
    reward: string
  }): Promise<TalentConfigTalent> => {
    return talentConfigApi.updateTalent(input.talentId, {
      bossId: input.bossId,
      category: input.category,
      tier: input.tier,
      description: input.description,
      reward: input.reward,
    })
  },
  deleteTalent: async (talentId: string): Promise<void> => {
    ensureEdenData(
      await http.api["talent-config"].talents({ talentId }).delete(),
      "Failed to delete talent",
    )
  },
}
